import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, MapPin, Clock, Trophy, ChevronRight, Vote } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useMatch } from "@/hooks/useMatch";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { InstallPrompt } from "@/components/InstallPrompt";
import { PushToggle } from "@/components/PushToggle";
import { fmtDate, fmtOpens, pct, generalSort } from "@/lib/football";

const fade = (i) => ({ initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, transition: { delay: i * 0.07 } });

const MatchCard = ({ data, act }) => {
  const nav = useNavigate();
  const m = data.match;
  const full = data.confirmed_count >= data.capacity;
  const st = data.me.status;
  const ratio = Math.min(100, (data.confirmed_count / data.capacity) * 100);
  const wlPos = data.players.find((p) => p.registration_status === "waiting_list" && p.id === data.me_id)?.waiting_list_position;
  const btn = "w-full rounded-2xl font-display font-black tracking-wide py-4 text-base active:scale-[0.98] transition-transform";
  let action;
  if (st === "confirmed") {
    action = (
      <>
        <p data-testid="registered-status" className="flex items-center justify-center gap-2 text-white font-bold"><Check className="w-5 h-5" /> Vous êtes inscrit</p>
        <button data-testid="view-match-button" onClick={() => nav("/matchs")} className={`${btn} bg-zinc-950 text-white mt-3`}>VOIR LE MATCH</button>
        {m.status === "scheduled" && <button data-testid="unregister-button" onClick={() => act("post", "/unregister", null, "Participation annulée")} className="w-full mt-2 text-xs text-white/80 underline">Annuler ma participation</button>}
      </>
    );
  } else if (st === "waiting_list") {
    action = (
      <>
        <p data-testid="waiting-status" className="text-center font-bold bg-amber-300 text-zinc-950 rounded-2xl py-3">Vous êtes sur la liste d'attente{wlPos ? ` (n°${wlPos})` : ""}</p>
        <button data-testid="leave-waitlist-button" onClick={() => act("post", "/unregister", null, "Vous avez quitté la liste d'attente")} className="w-full mt-2 text-xs text-white/80 underline">Quitter la liste d'attente</button>
      </>
    );
  } else if (!data.registration_open) {
    action = (
      <p data-testid="registration-closed" className="text-center text-sm font-semibold text-white bg-white/20 rounded-2xl py-3.5">
        {m.registration_mode === "closed" || new Date(m.registration_opens_at) < new Date() ? "Inscriptions fermées" : `Les inscriptions ouvrent ${fmtOpens(m.registration_opens_at)}`}
      </p>
    );
  } else if (full) {
    action = <button data-testid="join-waitlist-button" onClick={() => act("post", "/register", null, "Ajouté à la liste d'attente")} className={`${btn} bg-amber-300 text-zinc-950 shadow-lg`}>REJOINDRE LA LISTE D'ATTENTE</button>;
  } else {
    action = <button data-testid="register-button" onClick={() => act("post", "/register", null, "Vous êtes inscrit pour mardi !")} className={`${btn} bg-zinc-950 text-white shadow-lg shadow-sky-900/30`}>JE M'INSCRIS</button>;
  }
  return (
    <motion.section {...fade(0)} data-testid="next-match-card" className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-cyan-400 via-sky-500 to-blue-600 text-white p-5 shadow-xl shadow-sky-500/30">
      <div className="absolute -right-16 -top-16 w-52 h-52 rounded-full bg-white/15" />
      <div className="absolute right-10 -bottom-20 w-40 h-40 rounded-full bg-lime-300/25" />
      <p className="relative text-[11px] font-black tracking-[0.2em] text-white/85">PROCHAIN MATCH</p>
      <h1 data-testid="next-match-date" className="relative font-display text-3xl font-extrabold mt-1">{fmtDate(m.date)}</h1>
      <div className="relative flex gap-4 mt-2 text-sm text-white/85">
        <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{m.time}</span>
        <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{m.location}</span>
      </div>
      <div className="relative mt-5 flex items-end justify-between">
        <p data-testid="registered-count" className="num text-2xl font-bold">{data.confirmed_count} <span className="text-white/70">/ {data.capacity}</span></p>
        <p className="text-xs text-white/85">{full ? <b className="text-amber-200">Match complet</b> : "joueurs inscrits"}{data.waiting_count ? ` · ${data.waiting_count} en attente` : ""}</p>
      </div>
      <div className="relative h-2.5 rounded-full bg-white/25 mt-2 overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${ratio}%` }} transition={{ duration: 0.8 }} className={`h-full rounded-full ${full ? "bg-amber-300" : "bg-lime-300"}`} />
      </div>
      <div className="relative mt-5">{action}</div>
    </motion.section>
  );
};

const Players = ({ data }) => {
  const confirmed = data.players.filter((p) => p.registration_status === "confirmed");
  if (!confirmed.length) return null;
  return (
    <Link to="/matchs" data-testid="home-players-link" className="flex items-center gap-3 rounded-3xl bg-white shadow-sm border border-slate-200 p-4">
      <div className="flex -space-x-2.5">
        {confirmed.slice(0, 6).map((p) => <Avatar key={p.id} user={p} className="w-8 h-8 text-[10px]" ring="border-2 border-white" />)}
      </div>
      <p className="flex-1 text-sm text-slate-600">{data.draft.complete ? "Équipes prêtes — voir le terrain" : `${confirmed.length} joueurs inscrits`}</p>
      <ChevronRight className="w-5 h-5 text-slate-500" />
    </Link>
  );
};

const LastResult = ({ h }) => (
  <Link to={`/matchs/${h.id}`} data-testid="last-result-card" className="block rounded-3xl bg-white shadow-sm border border-slate-200 p-4">
    <p className="text-[11px] font-black tracking-[0.2em] text-slate-500">DERNIER RÉSULTAT · {fmtDate(h.date).toUpperCase()}</p>
    <div className="flex items-center justify-center gap-4 mt-3">
      <span className="text-xs font-black bg-white text-zinc-950 border border-slate-300 rounded-full px-3 py-1">BLANC</span>
      <span className="num text-3xl font-black">{h.white_score} - {h.black_score}</span>
      <span className="text-xs font-black bg-zinc-950 text-white rounded-full px-3 py-1">NOIR</span>
    </div>
    {h.motm_winner && <p className="text-center text-xs text-orange-600 mt-3 flex items-center justify-center gap-1"><Trophy className="w-3.5 h-3.5" /> Homme du Match : {h.motm_winner.short_name}</p>}
  </Link>
);

const MyStats = ({ s }) => (
  <Link to="/profil" data-testid="my-stats-card" className="block rounded-3xl bg-white shadow-sm border border-slate-200 p-4">
    <p className="text-[11px] font-black tracking-[0.2em] text-slate-500">MES STATISTIQUES</p>
    <div className="grid grid-cols-4 gap-2 mt-3 text-center">
      {[["Points", s.points, "bg-cyan-50 text-cyan-700"], ["Matchs", s.played, "bg-violet-50 text-violet-700"], ["Victoires", pct(s.win_rate), "bg-lime-50 text-lime-700"], ["Buts", s.goals, "bg-orange-50 text-orange-700"]].map(([l, v, c]) => (
        <div key={l} className={`rounded-2xl py-2 ${c}`}><p className="num text-xl font-bold">{v}</p><p className="text-[10px] opacity-80">{l}</p></div>
      ))}
    </div>
  </Link>
);

const TopRanking = ({ players }) => (
  <Link to="/statistiques" data-testid="home-ranking-card" className="block rounded-3xl bg-white shadow-sm border border-slate-200 p-4">
    <div className="flex justify-between items-center">
      <p className="text-[11px] font-black tracking-[0.2em] text-slate-500">CLASSEMENT GÉNÉRAL</p>
      <ChevronRight className="w-4 h-4 text-slate-500" />
    </div>
    <div className="mt-3 space-y-2">
      {players.map((p, i) => (
        <div key={p.id} className="flex items-center gap-3">
          <span className={`num w-5 text-sm font-bold ${i === 0 ? "text-amber-600" : "text-slate-500"}`}>{i + 1}</span>
          <Avatar user={p} className="w-8 h-8 text-[10px]" />
          <span className="flex-1 text-sm font-semibold">{p.short_name}</span>
          <span className="num text-sm font-bold">{p.points} pts</span>
        </div>
      ))}
    </div>
  </Link>
);

export default function Home() {
  const { user } = useAuth();
  const { data, act } = useMatch("current", 30000);
  const [hist, setHist] = useState([]);
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/matches/history").then((r) => setHist(r.data.filter((h) => h.status === "completed"))).catch((e) => toast.error(errMsg(e)));
    api.get("/stats").then((r) => setStats(r.data.players)).catch(() => {});
  }, []);
  const last = hist[0];
  const vote = hist.find((h) => h.motm_voting_open);
  const mine = stats?.find((s) => s.id === user.id);
  const top = stats ? [...stats].filter((s) => s.played).sort(generalSort).slice(0, 5) : [];
  return (
    <div className="space-y-4" data-testid="home-page">
      <InstallPrompt />
      <PushToggle banner />
      {data ? <MatchCard data={{ ...data, me_id: user.id }} act={act} /> : <div className="h-72 rounded-[28px] bg-white shadow-sm animate-pulse" />}
      {vote && (
        <motion.div {...fade(1)}>
          <Link to={`/matchs/${vote.id}`} data-testid="motm-vote-banner" className="flex items-center gap-3 rounded-3xl bg-gradient-to-r from-amber-300 to-orange-400 text-zinc-950 p-4 font-bold shadow-lg shadow-orange-400/25">
            <Vote className="w-6 h-6" /><span className="flex-1">Le vote Homme du Match est ouvert</span><ChevronRight className="w-5 h-5" />
          </Link>
        </motion.div>
      )}
      {data && <motion.div {...fade(2)}><Players data={data} /></motion.div>}
      {last && <motion.div {...fade(3)}><LastResult h={last} /></motion.div>}
      {mine && <motion.div {...fade(4)}><MyStats s={mine} /></motion.div>}
      {top.length > 0 && <motion.div {...fade(5)}><TopRanking players={top} /></motion.div>}
    </div>
  );
}
